import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RiMenu2Line } from "react-icons/ri";
import { BsSearch } from "react-icons/bs";

import { IconButton, cn } from "../../ui";
import CommandPalette from "./CommandPalette";
import LogoutDialog from "./LogoutDialog";
import UserMenu from "./UserMenu";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useNavigationContext } from "../../context/NavigationContext";
import type { Role } from "../sidebar/types";

type Props = {
  title?: string;
};

const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

const NavBar: React.FC<Props> = ({ title }) => {
  const { showSideBar, setShowSideBar } = useNavigationContext();
  const { logOut, setSelectedMenu, sysAdmin } = useAdminContext();

  const firstName: string | undefined = sysAdmin?.first_name;
  const lastName: string | undefined = sysAdmin?.last_name;
  const email: string | undefined = sysAdmin?.email;
  const role = sysAdmin?.role as Role | undefined;

  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  // Global ⌘K / Ctrl+K listener — open the command palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const confirmLogout = () => {
    navigate("/login");
    setSelectedMenu(0);
    logOut();
  };

  const shortcut = isMac() ? "⌘K" : "Ctrl+K";

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur",
        "supports-[backdrop-filter]:bg-white/70",
      )}
    >
      <div className="flex h-14 items-center gap-3 px-4 sm:px-5">
        {/* Left — hamburger + title */}
        <div className="flex min-w-0 items-center gap-3">
          <IconButton
            aria-label="Open sidebar"
            onClick={() => setShowSideBar(!showSideBar)}
            variant="secondary"
            size="sm"
            className="xl:hidden"
          >
            <RiMenu2Line className="h-4 w-4" />
          </IconButton>

          <div className="hidden h-5 w-px bg-gray-200 xl:block" aria-hidden />

          {title ? (
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-gray-900">
              {title}
            </h1>
          ) : null}
        </div>

        {/* Centre — search trigger */}
        <div className="mx-auto hidden w-full max-w-md md:block">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={cn(
              "group flex w-full items-center gap-3 rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-left transition-colors",
              "hover:border-gray-300 hover:shadow-sm",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
            )}
          >
            <BsSearch className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-colors group-hover:text-brand" />
            <span className="flex-1 truncate text-[12.5px] text-gray-500">
              Search modules, people, sections…
            </span>
            <span className="inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-[1px] text-[10px] font-semibold text-gray-500">
              {shortcut}
            </span>
          </button>
        </div>

        {/* Right — status + quick search (mobile) + user */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <StatusPill />

          <IconButton
            aria-label="Open command palette"
            onClick={() => setPaletteOpen(true)}
            variant="secondary"
            size="sm"
            className="md:hidden"
          >
            <BsSearch className="h-3.5 w-3.5" />
          </IconButton>

          <UserMenu
            firstName={firstName}
            lastName={lastName}
            email={email}
            role={role}
            onSignOut={() => setLogoutOpen(true)}
          />
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        role={role}
      />

      <LogoutDialog
        open={logoutOpen}
        firstName={firstName}
        lastName={lastName}
        role={role}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
      />
    </header>
  );
};

const StatusPill: React.FC = () => (
  <span
    className="hidden items-center gap-1.5 rounded-full border border-emerald-200/60 bg-emerald-50/50 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-emerald-700 lg:inline-flex"
    title="All systems operational"
  >
    <span aria-hidden className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/60" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
    </span>
    Operational
  </span>
);

export default NavBar;
